// OnboardingStep1.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import {
  useAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useDarkMode } from "@/hooks/useDarkMode";
import Checkbox from "@/components/common/Checkbox";
import WideButton from "@/components/common/WideButton";
import { XIcon } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";

interface OnboardingStep1Props {
  title: string;
  subtitle: string;
  onClose: () => void;
}

const OnboardingStep1 = ({
  title,
  subtitle,
  onClose,
}: OnboardingStep1Props) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();
  const isDark = useDarkMode();
  //fetch data options to display
  const { accessibilityNeedOptions } = useAccessibility();
  const { modalStyle, modalClassName } = useModalHeight();
  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  //get and store selected options for this view
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    submitData?.accessibility_need_selection,
  );

  useEffect(() => {
    AnalyticsService.trackGAPagesHandler(
      "authPopupCreateAccountViewedCreateNewAccountAccessNeedsPage",
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountViewedCreateNewAccountAccessNeedsPage",
      {},
    );
  }, []);

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler("authPopupCreateAccountStep1Complete");
    AnalyticsService.trackMx("authPopupCreateAccountStep1Complete", {});

    setSubmitData((prev) => ({
      ...prev,
      accessibility_need_selection: selectedOption,
    }));

    //Move to step 2 for all cases
    setScreenIndex(AuthModalScreen.OnboardingStep2);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={modalStyle}
      className="flex flex-col rounded-xl bg-bg-brand-secondary-default overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={onClose}
          variant="closeModal"
          icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Close modal"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>
      <div className="px-8 md:px-16 flex-1 flex flex-col">
        <h2 className="text-fg-neutral-strong title-m-bold mt-8">
          {i18n.get("welcomeToSociability")}
        </h2>
        <p className="title-s-semibold text-fg-neutral-strong mt-4">
          {subtitle}
        </p>
        <hr className="w-full border-border-token-brand-minimal my-4" />
        <p className="title-s-semibold text-fg-neutral-strong">
          {i18n.get("doYouOrSomeoneYouKnowHaveAnyAccessibilityRequirements")}
        </p>
        <span className="body-l-semibold !text-fg-brand-subtle block mt-2">
          {i18n.get("chooseOne")}
        </span>
        <div className="flex flex-col mt-4">
          {accessibilityNeedOptions.map((option, index) => (
            <div key={index} className="my-2">
              <Checkbox
                checked={selectedOption === option}
                key={index}
                onCheck={(value) => {
                  if (value) {
                    setSelectedOption(option);
                  } else {
                    setSelectedOption(undefined);
                  }
                }}
                label={option}
              />
            </div>
          ))}
        </div>
        <div className="flex-1" />
        <div className="mb-8 items-center justify-center flex flex-col">
          <WideButton
            type="filled"
            onClick={() => onContinue()}
            disabled={!selectedOption}
          >
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep1;
